<script>
  var body_scroll_x = 0
  var body_scroll_y = 0

  import HitApi from '@/services/Api/HitApi'

  import PlayerAvatar from '@/components/Player/PlayerAvatar'
  import ModalBase from '@/components/Modals/ModalBase'
  import Comment from './HitComment'

  function getDefaultData() {
    return {
      bigimg: null,
      zoomimg: false,
      loading: false,
      visible: true,
      likes_open: false,
      likes_loading: false,
      likes: [],
      new_comment: '',
      comments: {},
      comments_first_load: true
    }
  }

  export default {
    components: {
      Comment,
      ModalBase,
      PlayerAvatar
    },

    props: {
      hit: {
        type: Object,
        required: true
      },

      player: {
        type: Object
      },

      isPublic: {
        type: Boolean,
        default: false
      },

      showDate: {
        type: Boolean,
        default: true
      }
    },

    watch: {
      hit() {
        this.setHit(true)
      }
    },

    data() {
      return getDefaultData()
    },

    created() {
      this.setHit()
    },

    destroyed() {
      this.closeImg()
    },

    computed: {
      link_path() {
        return this.hit.path.replace(/^https?:\/\//, '').replace(/\/$/, '')
      },

      html_description() {
        return this.$linkifyStr(this.hit.description)
      },

      plink() {
        return {
          name: 'player',
          params: {
            id: this.player ? this.player.slug : this.hit.player.slug
          }
        }
      }
    },

    methods: {
      setHit(reset) {
        if (reset) {
          Object.assign(this.$data, getDefaultData())
        }

        if (this.hit.comments instanceof Array && !isNaN(this.hit.comments_count)) {
          this.comments.total = this.hit.comments_count
          this.comments.data = this.hit.comments.reverse()
        }
      },

      onKeyup(e) {
        if (e.keyCode === 27) {
          this.closeImg()
        }
      },

      openImg(path) {
        this.bigimg = path
        body_scroll_x = window.scrollX
        body_scroll_y = window.scrollY
        document.documentElement.style.overflow = 'hidden'
        window.addEventListener('keyup', this.onKeyup)
      },

      closeImg() {
        if (!this.bigimg) {
          return
        }

        this.bigimg = null

        document.documentElement.style.overflow = ''
        window.scroll(body_scroll_x, body_scroll_y)
        window.removeEventListener('keyup', this.onKeyup)
      },

      like(hit) {
        hit.user_liked = !hit.user_liked
        hit.likes_count += hit.user_liked ? 1 : -1

        HitApi.toggleLike(hit.id)
          .catch(() => {
            hit.user_liked = !hit.user_liked
            hit.likes_count += hit.user_liked ? 1 : -1
          })
      },

      likeTag(hit, tag) {
        tag.user_liked = !tag.user_liked
        tag.pivot.likes_count += tag.user_liked ? 1 : -1

        HitApi.toggleTagLike(hit.id, tag.id)
          .catch(() => {
            tag.user_liked = !tag.user_liked
            tag.pivot.likes_count += tag.user_liked ? 1 : -1
          })
      },

      onCommentDeleted() {
        this.hit.comments_count--

        if (this.hit.comments_count <= 0) {
          this.hit.user_commented = false
        }
      },

      showLikes(hit) {
        this.likes_open = true
        this.likes_loading = true

        HitApi.getLikes(hit.id)
          .then(likes => (this.likes = likes))
          .finally(() => (this.likes_loading = false))
      },

      addComment(hit) {
        var content = this.new_comment

        this.loading = true

        hit.user_commented = true
        hit.comments_count++

        this.new_comment = ''

        HitApi.postComment(hit.id, content)
          .then(comment => {
            this.comments.data.push(comment)
            setTimeout(() => this.$scrollTo('#comment-' + comment.id), 0)
          })
          .catch(error => {
            hit.user_commented = false
            hit.comments_count--

            this.new_comment = content
            this.$handleErrors(error)
          })
          .finally(() => (this.loading = false))
      },

      loadMoreComments(hit) {
        this.loading = true

        HitApi.getNextComments(hit.id, this.comments.next_page_url)
          .then(comments => {
            comments.data = comments.data.reverse()

            if (this.comments_first_load) {
              this.comments_first_load = false
            } else {
              comments.data = comments.data.concat(this.comments.data)
            }

            this.comments = comments
            hit.comments_count = comments.total
          })
          .finally(() => (this.loading = false))
      },

      editHit(hit) {
        this.$store.commit('Modals/show', 'hit_' + hit.type.toLowerCase())
        this.$store.commit('Hits/setEdited', hit)
      },

      deleteHit(hit) {
        if (confirm(this.$t('hit.delete_sure'))) {
          HitApi.deleteHit(hit.id).then(() => {
            this.visible = false
            this.user.player.hits_count--

            setTimeout(() => {
              this.$store.dispatch('Alerts/add', {
                trans: 'messages.hit_deleted',
                type: 'success'
              })
            })
          })
        }
      }
    }
  }
</script>

<template>
  <transition leave-active-class="fadeOut">
    <article class="Hit-card box animated" v-if="visible">
      <div class="Hit-card_header media">
        <router-link class="Hit-card_user-image media-left" :to="plink" tag="div">
          <player-avatar :player="player" size="48"/>
        </router-link>
        <div class="media-content">
          <div class="content">
            <p>
              <router-link class="Hit-card_user-name" :title="player.name" :to="plink" tag="strong">
                {{ player.name }}
              </router-link>
              <template v-if="showDate">
                -
                <small class="Hit-card_date">
                  {{ hit.created_at | moment("from", "now") }}
                </small>
              </template>
            </p>

            <p class="Hit-card_user-meta" :title="player.job">
              <span>{{ player.job }}</span>
              <br>
              <strong
                class="is-lastmeta"
                :title="player.school.name">
                {{ player.school.name }}
              </strong>
              <span class="is-lastmeta">-</span>
              <span class="is-lastmeta" :title="player.league_promo">
                {{ player.league_promo }}
              </span>
              <span v-if="hit.challenge"> - </span>
              <router-link
                class="is-challenge-link"
                tag="span"
                :to="{name: 'challenge', params: {id: hit.challenge.slug}}"
                :title="hit.challenge.name"
                v-if="hit.challenge"
                v-once>
                {{ $t('hit.posted_in_challenge', {name: hit.challenge.name}) }}
              </router-link>
            </p>
          </div>
        </div>
      </div>

      <div class="Hit-card_media">
        <a v-if="hit.type == 'IMAGE'"
          :href="hit.path"
          :class="{'lazy-init': hit.type == 'IMAGE'}"
          :title="$t('infotitle.zoom')"
          @click.prevent="openImg(hit.path)">
          <img v-lazy="hit.path" :alt="hit.title">
        </a>

        <iframe v-if="hit.type == 'CODE'"
          :src="hit.code_embed"
          style="width: 100%;"
          height="390"
          scrolling="no"
          frameborder="0"
          allowpaymentrequest
          allowtransparency="true"
          allowfullscreen></iframe>

        <iframe v-if="hit.type == 'VIDEO'"
          :src="hit.video_embed"
          style="width: 100%;"
          height="390"
          frameborder="0"
          allowfullscreen></iframe>

        <a v-if="hit.type == 'LINK'"
          class="title is-3 is-size-5-touch"
          :href="hit.path"
          target="_blank">
          {{ link_path }}
        </a>
      </div>

      <div class="Hit-card_userbar" v-if="!isPublic && hit.player_id == user.player.id">
        <button class="button is-text is-text-danger" @click="editHit(hit)">
          {{ $t('buttons.edit') }}
        </button>
        <button class="button is-text is-text-danger" @click="deleteHit(hit)">
          {{ $t('buttons.delete') }}
        </button>
      </div>

      <div class="Hit-card_content content">
        <h2 class="title is-5">{{ hit.title }}</h2>
        <p class="is-description" v-html="html_description"></p>
        <p class="tags" :title="$t('infotitle.hit_tags')">
          <span class="tag is-medium is-size-7-touch"
            :class="{'is-used': tag.user_liked, 'is-unused': !tag.user_liked}"
            v-for="tag in hit.tags"
            :key="tag.id"
            @click="likeTag(hit, tag)">
            <strong>+ {{ tag.name }}&nbsp;</strong> ({{ tag.pivot.likes_count }})
          </span>
        </p>
      </div>

      <template v-if="!isPublic">
        <div class="media comment-form">
          <form class="media-content" @submit.prevent="addComment(hit)">
            <p class="control" :class="{'is-loading': loading}">
              <textarea class="textarea"
                :class="{'is-open-small': new_comment}"
                ref="comment"
                :placeholder="$t('fields.add_comment')"
                v-model.trim="new_comment"></textarea>
            </p>
            <div class="comment-form_actions">
              <button class="button is-primary"
                :class="{'is-loading': loading}"
                v-show="new_comment"
                type="submit">
                {{ $t('buttons.send') }}
              </button>
            </div>
          </form>

          <div class="Hit-card_actions media-right">
            <a class="btn-like" :class="{'has-liked': hit.user_liked}" @click="like(hit)" :title="$t('infotitle.like')"></a>
            <a class="btn-like-counter" @click="showLikes(hit)">
              {{ hit.likes_count }}
            </a>
            <a class="btn-comment" :class="{'has-commented': hit.user_commented}" @click="$refs.comment.focus()" :title="$t('infotitle.comment')"></a>
            <a class="btn-comment-counter" @click="$refs.comment.focus()">
              {{ hit.comments_count }}
            </a>
          </div>
        </div>

        <p class="Hit-card_more-comments" v-if="comments.total > comments.data.length">
          <a @click="loadMoreComments(hit)">
            {{ $t('buttons.see_comments') }}
          </a>
        </p>
        
        <comment v-for="comment in comments.data" :key="comment.id" :id="'comment-' + comment.id" :comment="comment" @deleted="onCommentDeleted"></comment>
      </template>

      <div class="zoom" :class="{'is-zoomed': zoomimg}" v-if="bigimg" :title="$t('infotitle.zoom')">
        <img :src="bigimg" @click.prevent="zoomimg = !zoomimg">
        <a class="delete is-large" @click="closeImg()"></a>
      </div>

      <modal-base class="Hit-card_likes"
        :open="likes_open"
        @close="likes_open = false">
        <div class="modal-content box content">
          <p v-show="likes_loading">{{ $t('loading') }}</p>
          <p v-if="!likes_loading && !likes.length">{{ $t('empty') }}</p>
          <template v-if="!likes_loading">
            <router-link
              class="Hit-card_likes_item"
              v-for="like in likes"
              :key="like.id"
              :to="{name: 'player', params: {id: like.user.player.slug}}"
              tag="div"
              @click="likes_open = false">
              <player-avatar :player="like.user.player"/>
              {{ like.user.player.name }}
            </router-link>
          </template>
        </div>
        <button class="modal-close" @click="likes_open = false"></button>
      </modal-base>
    </article>
  </transition>
</template>

<style lang="scss">
  @import '~bulma/sass/utilities/all';

  .zoom {
    overflow: auto;
    position: fixed;
    z-index: 500;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: $grey-darker;

    img {
      display: block;
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
      margin: auto;
      object-fit: contain;
      cursor: zoom-in;
    }

    &.is-zoomed img {
      max-height: none;
      width: 100%;
      object-fit: initial;
      cursor: zoom-out;
    }

    .delete {
      position: fixed;
      top: 20px;
      right: 20px;
    }
  }

  .Hit-card {
    overflow: hidden;
    width: 100%;

    &.animated {
      animation-duration: 0.4s;
    }

    &.has-no-player {
      padding-top: 0;
    }

    .lazy-loaded {
      cursor: zoom-in;
    }

    .is-description {
      margin-bottom: pxToEm(20);
    }

    .tag {
      padding: pxToEm(18) pxToEm(20);
      font-size: $font-size-s;
      cursor: pointer;

      @include touch() {
        padding: pxToEm(16) pxToEm(16);
      }

      strong {
        margin-right: pxToEm(8);
        color: inherit;
      }
    }

    .is-lastmeta {
      color: $red;
    }

    .is-challenge-link {
      text-decoration: underline;
      cursor: pointer;
    }

    &_header {
      max-width: 100%;
      margin-bottom: 1.25rem;

      small {
        color: $grey;
      }

      strong {
        font-weight: 500;
      }

      .media-content p {
        &:first-child {
          margin-bottom: 0;
        }
      }
    }

    &_user {
      &-name,
      &-image {
        cursor: pointer;
      }

      &-date {
        color: $grey-light;
      }
    }

    &_media {
      max-height: 590px;
      margin-left: -1.25rem;
      margin-right: -1.25rem;

      .lazy-init:not(.lazy-loaded) {
        display: block;
        min-height: 360px;
      }

      > a {
        display: block;
        max-height: 590px;
      }

      img {
        object-fit: cover;
        max-height: 590px;
      }

      img,
      iframe {
        display: block;
        width: 100%;
        margin: 0;
      }

      .title {
        overflow: hidden;
        display: block;
        height: 120px;
        line-height: 120px;
        padding: 0 pxToEm(20);
        white-space: nowrap;
        text-overflow: ellipsis;
        color: $turquoise;
        background: $grey-lightest;
        border-bottom: 1px solid $turquoise;
      }

      @include touch() {
        .title {
          height: auto;
          line-height: inherit;
          padding: pxToEm(20);
        }
      }

      @include mobile() {
        iframe {
          height: 260px;
        }
      }
    }

    .comment-form {
      .textarea {
        border-width: 0;
        min-height: auto;
        height: 36px;
        padding-top: 0;
        padding-bottom: 0;
        resize: none;

        &.is-open-small {
          height: 70px;
          border-width: 1px;
        }

        &.is-open-big {
          height: 120px;
          border-width: 1px;
        }
      }

      &_actions {
        display: flex;
        justify-content: flex-end;
      }

      .button {
        margin-top: pxToEm(10);
        margin-left: pxToEm(10);
      }

      @include mobile() {
        flex-direction: column-reverse;

        .media-content,
        .media-right {
          width: 100%;
        }

        .media-right {
          margin: 0 0 pxToEm(10);
        }
      }
    }

    &_userbar {
      margin: 0 pxToEm(-20);
      text-align: right;
      border-bottom: 1px solid $grey-lighter;
    }

    &_content {
      margin-bottom: 0;
      padding: pxToEm(15) 0;
      word-break: break-word;
      word-wrap: break-word;

      &:not(:last-child) {
        border-bottom: 1px solid $grey-lighter;
      }

      p {
        white-space: pre-wrap;
      }
    }

    &_actions {
      display: flex;
      height: 36px;
      line-height: 36px;

      a {
        flex: 0 0 auto;
        text-align: right;
        color: $grey-light;

        &.btn-like,
        &.btn-comment {
          width: 30px;
          margin-left: 5px;
        }

        &.btn-like,
        &.btn-comment,
        &.btn-like-counter,
        &.btn-comment-counter {
          padding: 0 5px;
        }

        &.btn-like {
          background: transparent url('../../assets/img/icon_like_off.png') center center no-repeat;

          &.has-liked {
            background-image: url('../../assets/img/icon_like_on.png');
          }
        }

        &.btn-comment {
          background: transparent url('../../assets/img/icon_com_off.png') center center no-repeat;

          &.has-commented {
            background-image: url('../../assets/img/icon_com_on.png');
          }
        }

        &:hover {
          opacity: 0.6;
        }
      }
    }

    &_more-comments {
      margin-top: 1rem;
      text-align: right;
      border-top: 1px solid $grey-lighter;

      a {
        display: inline-block;
        margin: 1rem 0;
        text-decoration: underline;
        color: $grey;
      }
    }

    &_likes {
      .modal-content {
        max-width: 460px;
        max-height: 70%;
      }

      &_item {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        cursor: pointer;
        margin-bottom: pxToEm(10);

        figure {
          margin: 0;
        }
      }

      .image {
        margin-right: 0.5rem;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// HitCard.vue?4c363af3